import React, { useReducer } from 'react';
import { useOptimizelyFeature } from 'hooks/useOptimizelyFeature';
import { detectLocale } from 'i18n/detectLocale';
import { useI18N } from '../hooks/useI18N';
import {
  initialState,
  reducer,
  UPDATE_NEXT_LANGUAGE,
  UPDATE_RESOLVED_LANGUAGE,
} from './store';

type ProviderValue = {
  changeLocale: (newLocale: string) => void;
  locale: string;
  isLocaleLoading: boolean;
};

export const LocaleContext = React.createContext<ProviderValue>({
  changeLocale: () => {},
  locale: 'en',
  isLocaleLoading: false,
});

export function LocaleProvider({
  locale = 'en',
  children,
}: {
  locale?: string;
  children: React.ReactNode;
}): React.ReactElement {
  const [isI18nEnabled] = useOptimizelyFeature('i18n');
  const i18n = useI18N();

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    isInitialized,
    isLocaleLoading,
    nextLocale,
    resolvedLocale = locale,
  } = state;

  const changeLocale = (requestedlocale: string) => {
    // Safety check to not run the changeLanguage function when unneccessary
    if (requestedlocale === resolvedLocale) {
      /* eslint-disable-next-line no-console */
      console.log('The preferred language is already set: ', resolvedLocale);
      return;
    }

    dispatch({
      type: UPDATE_NEXT_LANGUAGE,
      value: requestedlocale,
    });
  };

  const setNextLocale = () => {
    if (!isInitialized) {
      return isI18nEnabled ? detectLocale() : locale;
    }

    return nextLocale;
  };

  const targetLocale = setNextLocale();
  if (isLocaleLoading) {
    i18n.changeLanguage(targetLocale, error => {
      if (error) {
        /* eslint-disable-next-line no-console */
        console.error(
          'There was an error updating the preferred language: ',
          error
        );
        return;
      }

      dispatch({ type: UPDATE_RESOLVED_LANGUAGE, value: targetLocale });
    });
  }

  return (
    <LocaleContext.Provider
      value={{
        changeLocale,
        locale: resolvedLocale || locale,
        isLocaleLoading,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
}
